
import {
  Quote,
  QuoteRequestType,
} from "functions/src/Payments/PaymentsInterfaces";
import {
  QuoteSubscriptionRequest,
  SubscriptionPromotion,
  subscriptionType,
} from "functions/src/Subscriptions/SubscriptionsInterfaces";
import {
  ACADEMY_SUBSCRIPTION_IDS,
  SUB_MMI_COLOMBIA,
  hasAnySubscriptionOf,
  isAcademyLicenseId,
  isAcademySubscriptionId,
  isVipSubscriptionId,
  maxRemainingDaysOf,
} from "functions/src/Subscriptions/packageIds";
import { Ref, defineComponent, ref } from "vue";
import { mapGetters } from "vuex";
import * as bs from "bootstrap";
import { quoteSubscription } from "@/store/Subscriptions/SubscriptionsConsts";
import { CheckoutModal } from "../../modals";

export default defineComponent({
  name: "create-quote",
  // Ver el comentario de la plantilla: sin esto, Vue intenta heredar solo y no
  // puede, porque hay dos raíces.
  inheritAttrs: false,
  props: {
    username: { type: String, required: true },
    email: { type: String, required: true },
    subscriptions: {
      type: Object as () => Map<string, number>,
      required: true,
    },
  },
  emits: ["quoteCreated", "error"],
  components: {
    CheckoutModal,
  },
  data() {
    return {
      update: false,
      isQuoting: false,
      quote: {} as Quote<QuoteRequestType>,
      modalObj: {} as any,
      activating: false,
    };
  },
  computed: {
    ...mapGetters("subs", {
      subs: "getSubscriptionsConfig",
      promotions: "getSubscriptionsPromotions",
    }),
    ...mapGetters("sys", ["isOffline"]),
    /**
     * «Usuario que estrena» = NUNCA tuvo academia, en NINGUNA generación.
     *
     * Es EXISTENCIA, no vigencia: un `trading-academy` vencido sigue existiendo
     * y esta persona no estrena nada. Y al revés, quien compró `gtr-fly` de cero
     * no tiene documento viejo: preguntando sólo por el viejo saldría como
     * estreno y se le ofrecería el GTR Win de primera compra.
     */
    isNew(): boolean {
      return !hasAnySubscriptionOf(
        this.subscriptions,
        ACADEMY_SUBSCRIPTION_IDS
      );
    },
    isVIP(): boolean {
      // `mmi-colombia` no se renombró: sigue siendo un doc-id único.
      return (this.subscriptions.get(SUB_MMI_COLOMBIA) ?? 0) > 0;
    },
    /**
     * Los días de academia que gobiernan, de la generación que más lejos llegue.
     * `undefined` = no tiene ninguna (ver `isNew`), y NO es lo mismo que 0.
     */
    academyDaysLeft(): number | undefined {
      return maxRemainingDaysOf(this.subscriptions, ACADEMY_SUBSCRIPTION_IDS);
    },
  },
  methods: {
    /**
     * Qué filas del catálogo puede cotizar el operador.
     *
     * ⚠️ POR FAMILIA, NO POR LITERAL. El catálogo tiene ahora las DOS
     * generaciones (`trading-academy` y `gtr-fly`, `gtr-vip` y `gtr-win`), y un
     * `switch` con los literales viejos manda las filas nuevas al
     * `default: return false`: el operador ve GTR Fly y GTR Win con el candado
     * puesto y ningún mensaje que lo explique.
     *
     * Lo que NO cambia: la familia LITE sigue fuera (cae al `default`), igual
     * que antes — esta herramienta nunca cotizó el Lite y ampliarla sería una
     * decisión de producto, no un arreglo del renombre.
     */
    canCreateQuote(subscriptionId: subscriptionType): boolean {
      // El id de conveniencia (academia + CMMI de una vez): sólo de estreno.
      if (isVipSubscriptionId(subscriptionId)) return this.isNew;
      // La academia: se renueva en los últimos 3 días. Sin academia ninguna,
      // `academyDaysLeft` es `undefined` y la primera compra queda abierta.
      if (isAcademySubscriptionId(subscriptionId)) {
        return (this.academyDaysLeft ?? 0) < 3;
      }
      if (subscriptionId === SUB_MMI_COLOMBIA) {
        return !this.isVIP && !this.isNew;
      }
      // La licencia de academia y su copia GLOBAL comparten regla: la global es
      // una copia de la de siempre. Sin esto, la global caería en el `false` y
      // el operador NO PODRÍA cotizarla — un botón muerto y sin explicación.
      if (isAcademyLicenseId(subscriptionId)) return !this.isNew;
      return false;
    },
    clearQuote() {
      this.quote = {} as Quote<QuoteRequestType>;
    },
    createQuote(forceUpdate = false) {
      if (
        this.selectedSubscription &&
        this.canCreateQuote(this.selectedSubscription.subscriptionId)
      ) {
        const p = this.selectedSubscription;
        const request: QuoteSubscriptionRequest = {
          username: this.username,
          email: this.email,
          duration: p.duration,
          durationUnit: p.durationUnit,
          isDirectAffiliate: false,
          subscriptionId: p.subscriptionId,
          promotionId: p._id,
          forceUpdate: forceUpdate,
        };
        if (!this.isOffline) {
          this.isQuoting = true;
          this.$store
            .dispatch("subs/" + quoteSubscription, request)
            .then((q) => {
              if (q.OPCODE == "ERROR") {
                alert(q.message);
              } else {
                this.quote = q;

                if (!this.modalObj._isShown) {
                  this.modalObj.show();
                }
              }
              this.isQuoting = false;
            });
        }
      }
    },
  },
  mounted() {
    this.modalObj = new bs.Modal(document.getElementById("checkout-modal"));
  },
  setup() {
    const selectedSubscription: Ref<SubscriptionPromotion | null> = ref(null);

    const selectSubscription = (subscription: SubscriptionPromotion) => {
      selectedSubscription.value = subscription;
    };

    return {
      selectedSubscription,
      selectSubscription,
    };
  },
});
